/**
 * Folding the data layer's failure modes into one renderable shape.
 *
 * Requests go through the cgt-backend proxy, which changes what each mode
 * means but not the shape:
 *
 *   - `{code, message}` bodies pass through the proxy verbatim: 400
 *     (unparseable ER7), 404 (unknown channel or an evicted message), 409
 *     (wrong channel direction), 413, 500.
 *   - the proxy's own 503/504 when the NAMED SOURCE is down — an `http`
 *     failure whose message names the source and states it was not silently
 *     substituted. This is the ticket's "explicit error, no fallback" state.
 *   - `{ok:false, reason}` at HTTP 200. That is a completed exchange the peer
 *     rejected — an OUTCOME, not a failure. It never reaches this module.
 *   - `fetch` rejecting outright, which now means the CGT API itself did not
 *     answer, not the data layer.
 */

export type FailureKind =
  /** The CGT API did not answer (or CORS refused). The data layer was never reached. */
  | 'unreachable'
  /** The message aged out of the ring buffer. Normal, not an error. */
  | 'evicted'
  /** An answered request: the source's own 4xx/5xx, or the proxy's 503/504 for a dead source. */
  | 'http'

export interface DataLayerFailure {
  kind: FailureKind
  /** 0 when the request never completed. */
  status: number
  /** Machine-readable code when the body carried one. */
  code: string | null
  /** Safe to render. Never empty. */
  message: string
}

export class DataLayerError extends Error {
  readonly failure: DataLayerFailure

  constructor(failure: DataLayerFailure) {
    super(failure.message)
    this.name = 'DataLayerError'
    this.failure = failure
  }
}

/** A `fetch` rejection, an abort, or anything else thrown before a response. */
export function networkFailure(error: unknown): DataLayerFailure {
  const aborted = error instanceof DOMException && error.name === 'TimeoutError'
  return {
    kind: 'unreachable',
    status: 0,
    code: aborted ? 'TIMEOUT' : null,
    // The CGT API, not the data layer: the proxy reports a dead SOURCE as its
    // own explicit 503/504, so a request that never completed means the hop
    // before it failed.
    message: aborted
      ? 'The CGT API did not respond in time.'
      : 'Cannot reach the CGT API. The Incoming tab reads the data layer through it.',
  }
}

/**
 * Normalize a non-2xx response body.
 *
 * `code` is deliberately read as an open string, not one of the five documented
 * values: a body over express.json's 100KB default arrives as 413 with
 * `code: 'INTERNAL_ERROR'` and a real message, and the mimic endpoint returns a
 * 500 whose message bypasses the service's usual 5xx scrubbing and must be
 * shown. Assuming a closed set would swallow both.
 */
export function normalizeFailure(
  status: number,
  body: unknown,
  context: { evictable?: boolean } = {},
): DataLayerFailure {
  const record = typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : null
  const code = typeof record?.code === 'string' ? record.code : null
  const rawMessage = typeof record?.message === 'string' ? record.message : null
  const textBody = typeof body === 'string' && body.trim() ? body.trim() : null

  // A 404 on a message detail is eviction, which is expected: the store is a
  // 500-message ring also bounded by bytes. Rendering it as an error would be a
  // lie about a healthy system.
  if (status === 404 && context.evictable) {
    return {
      kind: 'evicted',
      status,
      code,
      message: 'This packet is no longer in the buffer. The data layer keeps only the most recent messages.',
    }
  }

  return {
    kind: 'http',
    status,
    code,
    message: rawMessage ?? textBody ?? `The data layer returned ${status}.`,
  }
}

/**
 * Nothing answered, as opposed to something answering with an error. A source
 * that is down or unroutable is the normal state without the VPN route, so it
 * is not a fault. The data layer's own statuses top out at 500, so 502 is the
 * lower bound: above it, only the proxy speaks.
 */
export function isNotConnected(failure: DataLayerFailure): boolean {
  if (failure.kind === 'unreachable') return true
  return failure.kind === 'http' && failure.status >= 502 && failure.status <= 504
}

/** One line for a banner. Kept here so every surface words a failure the same way. */
export function failureHeadline(failure: DataLayerFailure): string {
  switch (failure.kind) {
    case 'unreachable':
      return 'Data layer unreachable'
    case 'evicted':
      return 'Packet no longer available'
    case 'http':
      return `Data layer error (${failure.status})`
  }
}
